import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "h-full w-full" }

import Swiper, { Autoplay, Mousewheel, type SwiperOptions } from 'swiper';
import {
    computed,
    watch,
    onMounted,
    provide,
    ref,
    useSlots,
    type VNode,
    type ComponentPublicInstance
} from 'vue';
import { v4 as uuid } from 'uuid';
import { getTwConfig } from '@/utils';

interface Props {
    direction?: 'horizontal' | 'vertical' | { [breakpoint: string]: 'horizontal' | 'vertical' };
    loop?: boolean;
    gap?: number | { [breakpoint: string]: number };
    containerClasses?: string;
    slideClasses?: string;
    wrapperClasses?: string;
    slideTag?: string;
    wrapperTag?: string;
    start?: number;
    speed?: number;
    slidesOffsetAfter?: number;
    centered?: boolean;
    autoplay?: boolean;
    autoplayDisableOnClick?: boolean;
    allowTouchMove?: boolean;
    autoHeight?: boolean;
    editMode?: boolean;
    slidesPerView?: number | string | { [breakpoint: string]: number | string };
    mousewheel?: boolean;
    slideToClickedSlide?: boolean;
    shuffleItems?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BizSlider',
  props: {
    direction: { default: () => ({ default: 'horizontal' }) },
    loop: { type: Boolean, default: false },
    gap: { default: () => 0 },
    containerClasses: { default: '' },
    slideClasses: { default: '' },
    wrapperClasses: { default: '' },
    slideTag: { default: 'div' },
    wrapperTag: { default: 'div' },
    start: { default: 0 },
    speed: { default: 300 },
    slidesOffsetAfter: { default: 0 },
    centered: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    autoplayDisableOnClick: { type: Boolean, default: true },
    allowTouchMove: { type: Boolean, default: true },
    autoHeight: { type: Boolean, default: false },
    editMode: { type: Boolean, default: false },
    slidesPerView: { default: () => 'auto' },
    mousewheel: { type: Boolean, default: false },
    slideToClickedSlide: { type: Boolean, default: true },
    shuffleItems: { type: Boolean, default: false }
  },
  emits: ["index-changed"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'index-changed', index: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { direction: 'horizontal' | 'vertical' | { [breakpoint: string]: 'horizontal' | 'vertical' }, loop: boolean, gap: number | { [breakpoint: string]: number }, containerClasses: string, slideClasses: string, wrapperClasses: string, slideTag: string, wrapperTag: string, start: number, speed: number, slidesOffsetAfter: number, centered: boolean, autoplay: boolean, autoplayDisableOnClick: boolean, allowTouchMove: boolean, autoHeight: boolean, editMode: boolean, slidesPerView: number | string | { [breakpoint: string]: number | string }, mousewheel: boolean, slideToClickedSlide: boolean, shuffleItems: boolean }





const slots = useSlots();
const twConfig = getTwConfig();

// Refs
const containerRef = ref<HTMLElement | null>(null);
const slides = ref<ComponentPublicInstance[]>([]);
const swiper = ref<Swiper | null>(null);
const currentIndex = ref(0);
const autoplayOn = ref(props.autoplay);
const goToClick = ref(false);
const uid = ref(uuid());

// Swiper options
const options = ref<SwiperOptions>({
    watchOverflow: true,
    wrapperClass: 'slider-wrapper',
    slideClass: 'slider-slide',
    observer: true,
    loop: false,
    speed: props.speed,
    allowTouchMove: props.allowTouchMove,
    autoplay: props.autoplay ? { delay: 5000 } : false,
    autoHeight: props.autoHeight,
    direction: 'horizontal',
    touchReleaseOnEdges: true,
    initialSlide: props.start,
    observeParents: true,
    mousewheel: props.mousewheel,
    watchSlidesProgress: true,
    slideActiveClass: 'slide-active',
    slideVisibleClass: 'slide-visible',
    slideToClickedSlide: props.slideToClickedSlide,
    threshold: 20
});

// Helper functions
const breakpointSize = (name: string): number => {
    if (Object.prototype.hasOwnProperty.call(twConfig.theme.screens, name)) {
        return parseInt(twConfig.theme.screens[name]);
    }
    return -1;
};

const setBreakpointOption = (value: any, name: string) => {
    const breakpoints = options.value.breakpoints || {};

    if (typeof value === 'number' || typeof value === 'string') {
        options.value[name] = value;
        return;
    }

    Object.entries(value).forEach(([key, val]) => {
        const bp = breakpointSize(key);
        if (bp > 0) {
            if (!Object.prototype.hasOwnProperty.call(breakpoints, bp)) {
                breakpoints[bp] = {};
            }
            breakpoints[bp][name] = val;
        } else {
            options.value[name] = val;
        }
    });

    if (!options.value.breakpoints) {
        options.value.breakpoints = breakpoints;
    }
};

// Slide registration
const registerSlide = (slide: ComponentPublicInstance) => {
    slides.value.push(slide);
};

provide('registerSlide', registerSlide);

// Index change handler
const handleIndexChange = ({
    activeIndex,
    realIndex
}: {
    activeIndex: number;
    realIndex: number;
}) => {
    currentIndex.value = props.loop || props.autoplay ? realIndex : activeIndex;

    // Workaround for swiper bug
    setTimeout(() => {
        swiper.value?.slideReset();
    });

    // Re-enable autoplay if needed
    if (props.autoplay && !goToClick.value && !swiper.value?.autoplay.running) {
        swiper.value?.autoplay.start();
        autoplayOn.value = true;
    }

    goToClick.value = false;
    emit('index-changed', currentIndex.value);
};

// Navigation methods
const next = () => {
    if (!swiper.value || swiper.value.isEnd) return;

    if (props.loop) {
        currentIndex.value =
            currentIndex.value + 1 >= swiper.value.slides.length ? 0 : currentIndex.value + 1;
    } else if (currentIndex.value + 1 < swiper.value.slides.length) {
        currentIndex.value++;
    }

    swiper.value.slideNext();
};

const prev = () => {
    if (!swiper.value || swiper.value.isBeginning) return;

    if (props.loop) {
        currentIndex.value =
            currentIndex.value > 0 ? currentIndex.value - 1 : swiper.value.slides.length - 1;
    } else if (currentIndex.value > 0) {
        currentIndex.value--;
    }

    swiper.value.slidePrev();
};

const goTo = (index: number) => {
    if (swiper.value) {
        swiper.value.slideTo(index);
        currentIndex.value = index;
    }

    if (props.autoplayDisableOnClick) {
        goToClick.value = true;
        autoplayOn.value = false;
        swiper.value?.autoplay.stop();
    }
};

const handleSlideClick = (e: any) => {
    const index = slides.value.findIndex(x => x.id === e.id);
    if (index >= 0 && index < slides.value.length) {
        goTo(index);
    }
};

// Base slides from slots (pure computation)
const renderedSlides = computed<VNode[]>(() => {
    if (!slots.default) return [];

    return slots
        .default()
        .flatMap(x => (typeof x.type === 'symbol' && Array.isArray(x.children) ? x.children : [x]))
        .filter(x => typeof (x as VNode).type === 'object');
});

const numSlides = computed(() => renderedSlides.value.length);

// Final slides used by the template
const finalSlides = computed<VNode[]>(() => {
    if (props.editMode || !props.shuffleItems) return renderedSlides.value;
    return [...renderedSlides.value].sort(() => Math.random() - 0.5);
});

// Direction classes
const wrapperDirectionClasses = computed(() => {
    if (typeof props.direction === 'string') {
        return props.direction === 'vertical' ? 'flex-col' : 'flex-row';
    }

    const classes: string[] = [];
    Object.entries(props.direction).forEach(([key, value]) => {
        const bp = breakpointSize(key);
        const dir = value === 'vertical' ? 'flex-col' : 'flex-row';

        if (bp > 0) {
            classes.push(`${key}:${dir}`);
        } else {
            classes.push(dir);
        }
    });

    return classes.join(' ');
});

// Watchers
watch(
    () => props.start,
    () => {
        goTo(props.start);
    }
);

// Lifecycle
onMounted(() => {
    if (props.editMode) return;

    setBreakpointOption(props.direction, 'direction');
    setBreakpointOption(props.gap, 'spaceBetween');
    setBreakpointOption(props.slidesPerView, 'slidesPerView');
    setBreakpointOption(props.slidesOffsetAfter, 'slidesOffsetAfter');
    setBreakpointOption(props.centered, 'centeredSlides');

    if (props.autoplay) {
        Swiper.use([Autoplay]);
    }

    if (props.mousewheel) {
        Swiper.use([Mousewheel]);
    }

    swiper.value = new Swiper(containerRef.value as HTMLElement, options.value);
    swiper.value.on('activeIndexChange', handleIndexChange);
});

expose({
    swiper,
    slides,
    currentIndex,
    autoplayOn,
    uid,
    goTo,
    prev,
    next,
    containerRef,
    numSlides
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "containerRef",
      ref: containerRef,
      class: _normalizeClass(
                ['slider-container h-full w-full overflow-hidden', __props.containerClasses]
                    .filter(Boolean)
                    .join(' ')
            )
    }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent(__props.wrapperTag), {
        class: _normalizeClass(
                    [
                        'slider-wrapper',
                        __props.editMode ? '' : 'flex h-full flex-nowrap',
                        _unref(wrapperDirectionClasses),
                        __props.wrapperClasses,
                        __props.autoHeight ? 'items-start' : ''
                    ]
                        .filter(Boolean)
                        .join(' ')
                )
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(finalSlides), (el, i) => {
            return (_openBlock(), _createBlock(_resolveDynamicComponent(__props.slideTag), {
              key: i,
              class: _normalizeClass(`slider-slide max-w-full shrink-0 ${__props.slideClasses}${
                        currentIndex.value === i ? ' slide-active' : ''
                    }`)
            }, {
              default: _withCtx(() => [
                (_openBlock(), _createBlock(_resolveDynamicComponent(el), { onSlideClick: handleSlideClick }, null, 32))
              ]),
              _: 2
            }, 1032, ["class"]))
          }), 128))
        ]),
        _: 1
      }, 8, ["class"]))
    ], 2),
    (_ctx.$slots.pagination && !__props.editMode)
      ? _renderSlot(_ctx.$slots, "pagination", {
          key: 0,
          index: currentIndex.value + 1,
          total: _unref(numSlides),
          slides: slides.value,
          go: goTo,
          prev: prev,
          next: next,
          uid: uid.value,
          autoplay: autoplayOn.value
        })
      : _createCommentVNode("", true),
    (_ctx.$slots.controls)
      ? _renderSlot(_ctx.$slots, "controls", {
          key: 1,
          prev: prev,
          next: next,
          total: _unref(numSlides),
          index: currentIndex.value,
          beginning: swiper.value ? swiper.value.isBeginning : false,
          end: swiper.value ? swiper.value.isEnd : false
        })
      : _createCommentVNode("", true)
  ]))
}
}

})